import React, { useState, useEffect, useMemo } from 'react'
import { Button, Typography } from '@equinor/eds-core-react'
import { useGetOnboardedSensorsPage } from '@/core/hooks-and-services/onboardedSensorsService'
import { OnboardedSensorsPageParams } from '@/api/apiService'
import { Sensor } from '@/features/dashboard/models'
import { SensorCard } from '@/features/dashboard/components/sensorCards'
import {
    BackgroundContainer,
    AboutContainer,
    CardImageWrapper,
    HeaderContainer,
    ProjectAndPlantContainer,
    SelectDropDown,
    SaveAndGoToIIoT,
    ReadMoreButton,
    RecentlyAddedSensorsContainer,
    SensorCardsContainer,
    HeaderRecentlyAdded,
    ContentContainer,
} from '@/components/styles/FrontPageStyles'

import { StyledLink } from '@/components/styles/AboutStyles'
import { useGetUserSettings } from '@/core/hooks-and-services/userSettingsService'
import { InstalledSensorStatus, PlantDto } from '@/core/models/types'
import { addSnackBarMessage, currentPlantAtom } from '@/core/store/globalStore'
import { useAtomValue } from 'jotai'
import { ALL_PLANTS_OPTION, normalizePlantSelection, useCurrentPlant } from '@/core/hooks-and-services/useCurrentPlant'
import { useGetDevices } from '@/core/hooks-and-services/devicesService'
import { useNavigate } from 'react-router-dom'
import { Icon } from '@equinor/eds-core-react'
import styled from 'styled-components'
import { useWindowWidth } from '@/hooks/useWindowWidth'
import { mobileActionBarBottom } from '@/styles/layout'

interface ISensorCards {
    sensors: Sensor[]
    onRefetchSensors: () => void
    isFetchingSensors: boolean
    filters: {
        plant: string
    }
}

const ChoosePlant = () => {
    const { currentPlant, setCurrentPlant, plants, isHydrated } = useCurrentPlant()
    const [selectedPlant, setSelectedPlant] = useState<PlantDto | undefined>(ALL_PLANTS_OPTION)

    useEffect(() => {
        if (isHydrated) setSelectedPlant(currentPlant ?? ALL_PLANTS_OPTION)
    }, [isHydrated, currentPlant])

    const handlePlantChange = (changes: { selectedItems: unknown[] }) => {
        setSelectedPlant(changes.selectedItems[0] as PlantDto | undefined)
    }

    const handleSaveAndGoToIIoTClick = async () => {
        const ok = await setCurrentPlant(normalizePlantSelection(selectedPlant))
        if (ok) addSnackBarMessage(`User settings saved`)
    }

    return (
        <ProjectAndPlantContainer>
            <Typography variant="h6">Choose plant</Typography>
            <Typography>
                For registration of any devices to a specific plant, please select a plant. Make sure you have correct
                authentication at{' '}
                <StyledLink href="https://accessit.equinor.com/Search/Search?term=iiot">AccessIT</StyledLink>. This user
                setting will be remembered. If you work on several plants, you can update this setting in your user
                profile and settings.
            </Typography>
            <SelectDropDown
                options={[ALL_PLANTS_OPTION, ...plants]}
                optionLabel={(plant: unknown) => (plant as PlantDto).description ?? ''}
                selectedOptions={selectedPlant ? [selectedPlant] : []}
                onOptionsChange={handlePlantChange}
                label="Select plant"
                placeholder="Select"
                autoWidth
            ></SelectDropDown>
            <SaveAndGoToIIoT onClick={handleSaveAndGoToIIoTClick}>Save</SaveAndGoToIIoT>
        </ProjectAndPlantContainer>
    )
}

const SensorCards = React.memo((props: ISensorCards) => {
    const { sensors, filters = { plant: '', status: '' } } = props
    const { data: devices } = useGetDevices()
    const navigate = useNavigate()
    const itemsPerPage = 3

    const filteredSensors = useMemo(() => {
        return sensors
            .filter((sensor) => {
                const onboardedStatuses =
                    sensor.status === InstalledSensorStatus.Active ||
                    sensor.status === InstalledSensorStatus.Prepared ||
                    sensor.status === InstalledSensorStatus.Offline

                // '' means "All plants" — no plant restriction. Otherwise strict equality on the exact PlantDto.shortName.
                const matchesPlant = filters.plant === '' || sensor.plant === filters.plant

                return onboardedStatuses && matchesPlant
            })
            .sort((a, b) => {
                const dateA = a.installationDate ? new Date(a.installationDate).getTime() : null
                const dateB = b.installationDate ? new Date(b.installationDate).getTime() : null
                return (dateB ?? 0) - (dateA ?? 0)
            })
    }, [sensors, filters])

    const paginatedData = useMemo(() => {
        return filteredSensors.slice(0, itemsPerPage)
    }, [filteredSensors])

    const handleCardClick = (deviceKey: string) => {
        const encodedDeviceKey = encodeURIComponent(deviceKey)
        navigate(`/portfolio/${encodedDeviceKey}`)
    }

    return (
        <div style={{ width: '100%' }}>
            <SensorCardsContainer>
                {paginatedData.map((sensor) => {
                    if (!sensor.sensorModel) return
                    // Find the corresponding device based on sensorModel
                    const device = devices.find((device) => device.key === sensor.sensorModel)
                    if (!device || !device.url || !device.key) return

                    return (
                        <SensorCard
                            key={sensor.sensorDbKey}
                            imageSrc={device ? device.url : '/sensor_images/TempSensorTWTG.png'}
                            sensor={sensor}
                            showDetailsButton={true}
                            toSensorPortfolio={() => device && device.key && handleCardClick(device.key)}
                        />
                    )
                })}
            </SensorCardsContainer>
        </div>
    )
})

const RecentlyAdded = ({ userPlant }: { userPlant: string }) => {
    const pagedParams = useMemo<OnboardedSensorsPageParams>(
        () => ({
            page: 1,
            pageSize: 3,
            sortBy: 'installationDate',
            sortDir: 'desc',
            status: [InstalledSensorStatus.Active, InstalledSensorStatus.Prepared, InstalledSensorStatus.Offline],
            plant: userPlant || undefined,
        }),
        [userPlant]
    )
    const { data, isLoading, refetch } = useGetOnboardedSensorsPage(pagedParams)
    const windowWidth = useWindowWidth()
    const navigate = useNavigate()

    const handleAddNewSensorClick = () => {
        navigate('/dashboard', {
            state: {
                openAddSensorModal: true,
            },
        })
    }

    return (
        <RecentlyAddedSensorsContainer>
            <HeaderRow>
                <HeaderRecentlyAdded variant="h6">Recently added sensors</HeaderRecentlyAdded>
                {windowWidth > 1024 ? (
                    <DesktopButtonSlot>
                        <AddNewSensorButton color={'primary'} onClick={handleAddNewSensorClick}>
                            <Icon name="add"></Icon>
                            Add new sensor
                        </AddNewSensorButton>
                    </DesktopButtonSlot>
                ) : (
                    <MobileFloatingContainer>
                        <AddNewSensorButton color={'primary'} onClick={handleAddNewSensorClick}>
                            <Icon name="add"></Icon>
                            {windowWidth > 330 ? 'Add sensor' : ''}
                        </AddNewSensorButton>
                    </MobileFloatingContainer>
                )}
            </HeaderRow>
            <SensorCards
                sensors={data ?? []}
                onRefetchSensors={refetch}
                isFetchingSensors={isLoading}
                filters={{ plant: userPlant }}
            />
        </RecentlyAddedSensorsContainer>
    )
}

function FrontPage() {
    const { data } = useGetUserSettings()
    const currentPlant = useAtomValue(currentPlantAtom)
    const navigate = useNavigate()
    const windowWidth = useWindowWidth()

    const filterPlant = currentPlant?.shortName ?? data?.plant ?? ''

    return (
        <>
            <BackgroundContainer>
                <HeaderContainer>
                    {windowWidth > 1024 ? (
                        <>
                            <Typography variant="h1" token={{ textAlign: 'center' }}>
                                Welcome to
                                <br />
                                Industrial Internet of Things (IIoT)
                            </Typography>
                        </>
                    ) : (
                        <>
                            <Typography variant="h1" token={{ textAlign: 'left' }}>
                                Welcome to IIoT
                            </Typography>
                        </>
                    )}
                </HeaderContainer>
                <ResponsiveContentContainer>
                    {data?.plant ? <RecentlyAdded userPlant={filterPlant} /> : <ChoosePlant />}
                    <AboutContainer>
                        <Typography
                            variant="h6"
                            token={{ textAlign: 'left' }}
                            style={{ alignSelf: 'flex-start', width: '100%' }}
                        >
                            About Industrial Internet of Things
                        </Typography>
                        <Typography>
                            This application is your go-to tool for exploring the growing number of IIoT sensors
                            available at Equinor. It makes onboarding easy by helping you document and register sensors
                            across various platforms like STID and Omnia Timeseries. Plus, if you provide coordinates,
                            you can also find the sensors in Echo!
                        </Typography>
                        <ReadMoreButton onClick={() => navigate('/about')}>Read more</ReadMoreButton>
                        <CardImageWrapper>
                            <img src={'/IIoT_logo.png'} />
                        </CardImageWrapper>
                    </AboutContainer>
                </ResponsiveContentContainer>
            </BackgroundContainer>
        </>
    )
}

export default FrontPage

const AddNewSensorButton = styled(Button)`
    width: 100%;
    height: 38px;
`

const MobileFloatingContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    margin: 0 0 1rem 0;
    align-items: center;

    @media (max-width: 1024px) {
        width: calc(100% - 32px);
        flex-wrap: nowrap;
        position: fixed;
        bottom: ${mobileActionBarBottom};
        left: 50%;
        transform: translateX(-50%);
        z-index: 9;
        border-radius: 8px;
        box-sizing: border-box;
        padding: 8px 8px;
        justify-content: space-between;
        background-color: #ffffff;
        border: 1px solid #d4d4d4;
        box-shadow: 0px 2px 4px 0px #00000024;
    }
`

const ResponsiveContentContainer = styled(ContentContainer)`
    @media (max-width: 1024px) {
        padding-bottom: 7rem;
    }
`

const DesktopButtonSlot = styled.div`
    height: 40px;

    @media (max-width: 1024px) {
        display: none;
    }
`

const HeaderRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
`
